import { StepRun } from '@/types/stepRun';
import { StepRunStatusData } from '@/types/stepRunStatus';
import {
    SummaryViewResponseAPI,
    VersionSummaryViewDataItemAPI,
    SummaryViewDataRowAPI,
    SummaryViewPropertyBase,
    StatusSummaryDataAPI,
    ArchetypeSummaryViewProperty,
    StatisticSummaryViewProperty,
} from '@/types/summaryView';
import { WorkflowOverview, WorkflowOverviewWorkflowRun } from '@/types/workflowOverview';
import { AppHome, CompletedReleaseSummary, NextReleaseSummary, UpcomingReleaseSummary, AppSettings } from '@/types';
import { StringKeyedDictionary } from '@/typescriptHelpers';

export const convertWorkflowOverviewToSummaryView = (
    workflowOverview: WorkflowOverview,
    stepRunsDemoData: StringKeyedDictionary<StepRun> = {},
): SummaryViewResponseAPI => {
    const versionSummaryViewItems: VersionSummaryViewDataItemAPI[] = [];

    workflowOverview.workflowRuns.forEach((workflowRun) => {
        workflowOverview.selectedDestinationStepIds.forEach((destinationId) => {
            const rows = buildRowsForVersionAndDestination(workflowRun, destinationId, stepRunsDemoData);

            versionSummaryViewItems.push({
                version: workflowRun.version,
                destinationId,
                rows,
            });
        });
    });

    return {
        versionSummaryViewItems,
        availableTotalCount: versionSummaryViewItems.length,
        selectedTotalCount: versionSummaryViewItems.length,
    };
};

const buildRowsForVersionAndDestination = (
    workflowRun: WorkflowOverviewWorkflowRun,
    destinationId: string,
    stepRunsDemoData: StringKeyedDictionary<StepRun>,
): SummaryViewDataRowAPI[] => {
    const rows: SummaryViewDataRowAPI[] = [];

    rows.push({
        type: SummaryViewPropertyBase.WorkflowRunVersion,
        data: workflowRun.version,
    });

    const statusData: StatusSummaryDataAPI = {
        type: workflowRun.type,
        status: workflowRun.status,
        timelinePhase: workflowRun.timelinePhase,
        phasedReleaseInfo: workflowRun.phasedReleaseInfosByDestinationStepId?.[destinationId],
    };
    rows.push({
        type: SummaryViewPropertyBase.Status,
        data: statusData,
    });

    if (workflowRun.releasePilotId) {
        rows.push({
            type: SummaryViewPropertyBase.ReleasePilot,
            data: workflowRun.releasePilotId,
        });
    }

    if (workflowRun.featuresStep) {
        rows.push({
            type: SummaryViewPropertyBase.FeatureCompletePercent,
            data: workflowRun.featuresStep,
        });
    }

    if (workflowRun.targetKickoffDate) {
        rows.push({
            type: SummaryViewPropertyBase.TargetKickoffDates,
            data: workflowRun.targetKickoffDate,
        });
    }

    if (workflowRun.targetSubmitDate) {
        rows.push({
            type: SummaryViewPropertyBase.TargetSubmitDates,
            data: workflowRun.targetSubmitDate,
        });
    }

    if (workflowRun.targetReleaseDate) {
        rows.push({
            type: SummaryViewPropertyBase.TargetReleaseDates,
            data: workflowRun.targetReleaseDate,
        });
    }

    if (workflowRun.kickedOffAt) {
        rows.push({
            type: SummaryViewPropertyBase.KickedOffDate,
            data: workflowRun.kickedOffAt,
        });
    }

    const submittedAt = workflowRun.submittedAtByDestinationStepId?.[destinationId] || workflowRun.submittedAt;
    if (submittedAt) {
        rows.push({
            type: SummaryViewPropertyBase.SubmittedDate,
            data: submittedAt,
        });
    }

    const releasedAt = workflowRun.releasedAtByDestinationStepId?.[destinationId] || workflowRun.releasedAt;
    if (releasedAt) {
        rows.push({
            type: SummaryViewPropertyBase.ReleasedAtDate,
            data: releasedAt,
        });
    }

    const builds = workflowRun.selectedAppStoreBuildsByDestinationStepId?.[destinationId];
    if (builds && builds.length > 0) {
        rows.push({
            type: SummaryViewPropertyBase.Builds,
            data: builds,
        });

        rows.push({
            type: SummaryViewPropertyBase.AdoptionRate,
            data: builds,
        });
    }

    const phasedRelease = workflowRun.phasedReleaseInfosByDestinationStepId?.[destinationId];
    if (phasedRelease) {
        rows.push({
            type: SummaryViewPropertyBase.PhasedRelease,
            data: phasedRelease,
        });
    }

    if (workflowRun.workflowRunGraph?.stepGroups) {
        workflowRun.workflowRunGraph.stepGroups.forEach((stepGroup) => {
            stepGroup.stepArchetypeGroups.forEach((archetypeGroup) => {
                archetypeGroup.stepRunStatusDatum.forEach((stepRunStatus) => {
                    const stepRunId = stepRunStatus.stepRunId;
                    if (!stepRunId) return;

                    const destinationsForStep = workflowRun.stepRunIdToDestinationStepConfigIdsMap?.[stepRunId] || [];
                    if (!destinationsForStep.includes(destinationId)) return;

                    const fullStepRun = stepRunsDemoData[stepRunId];
                    if (!fullStepRun) return;

                    rows.push({
                        type: `Archetype:${archetypeGroup.archetype}` as typeof ArchetypeSummaryViewProperty,
                        data: fullStepRun as unknown as StepRunStatusData,
                    });
                });
            });
        });
    }

    if (workflowRun.stats && workflowRun.stats.length > 0) {
        workflowRun.stats.forEach((stat) => {
            rows.push({
                type: `Statistic:${stat.type}` as typeof StatisticSummaryViewProperty,
                data: stat,
            });
        });
    }

    return rows;
};

export const generateMockSummaryViewForApps = (
    workflowOverviews: WorkflowOverview[],
): Record<string, SummaryViewResponseAPI> => {
    const summaryViewByRequestId: Record<string, SummaryViewResponseAPI> = {};

    workflowOverviews.forEach((workflowOverview) => {
        const { appId } = workflowOverview;
        const appRequestId = `org-summary-demo-${appId}`;

        summaryViewByRequestId[appRequestId] = convertWorkflowOverviewToSummaryView(workflowOverview);
    });

    return summaryViewByRequestId;
};

export const convertAppHomeToSummaryView = (appHome: AppHome, appSettings: AppSettings): SummaryViewResponseAPI => {
    const versionSummaryViewItems: VersionSummaryViewDataItemAPI[] = [];
    const allReleases: Array<CompletedReleaseSummary | NextReleaseSummary | UpcomingReleaseSummary> = [];

    if (appHome.liveRelease) {
        allReleases.push(appHome.liveRelease);
    }
    if (appHome.nextRelease) {
        allReleases.push(appHome.nextRelease);
    }
    if (appHome.upcomingReleases) {
        allReleases.push(...appHome.upcomingReleases);
    }
    if (appHome.pastReleases) {
        allReleases.push(...appHome.pastReleases);
    }

    allReleases.forEach((release) => {
        const rows = buildRowsForReleaseSummary(release);
        const { destinations } = appSettings;
        versionSummaryViewItems.push({
            version: release.version,
            destinationId: destinations[0].stepConfigId, // Single destination for legacy
            rows,
        });
    });

    return {
        versionSummaryViewItems,
        availableTotalCount: versionSummaryViewItems.length,
        selectedTotalCount: versionSummaryViewItems.length,
    };
};

const buildRowsForReleaseSummary = (
    release: CompletedReleaseSummary | NextReleaseSummary | UpcomingReleaseSummary,
): SummaryViewDataRowAPI[] => {
    const rows: SummaryViewDataRowAPI[] = [];

    rows.push({
        type: SummaryViewPropertyBase.WorkflowRunVersion,
        data: release.version,
    });
    const statusData = {
        type: 'type' in release ? release.type : undefined,
        status: 'status' in release ? release.status : undefined,
        timelinePhase: undefined,
        phasedReleaseInfo: undefined,
    } as unknown as StatusSummaryDataAPI;
    rows.push({
        type: SummaryViewPropertyBase.Status,
        data: statusData,
    });

    if (release.releasePilotId) {
        rows.push({
            type: SummaryViewPropertyBase.ReleasePilot,
            data: release.releasePilotId,
        });
    }

    if (release.featuresStep) {
        rows.push({
            type: SummaryViewPropertyBase.FeatureCompletePercent,
            data: release.featuresStep,
        });
    }

    if (release.targetKickoffDate) {
        rows.push({
            type: SummaryViewPropertyBase.TargetKickoffDates,
            data: release.targetKickoffDate,
        });
    }

    if (release.targetSubmitDate) {
        rows.push({
            type: SummaryViewPropertyBase.TargetSubmitDates,
            data: release.targetSubmitDate,
        });
    }

    if (release.targetReleaseDate) {
        rows.push({
            type: SummaryViewPropertyBase.TargetReleaseDates,
            data: release.targetReleaseDate,
        });
    }

    const nextOrUpcoming = release as NextReleaseSummary | UpcomingReleaseSummary;
    if ('kickedOffAt' in nextOrUpcoming && nextOrUpcoming.kickedOffAt) {
        rows.push({
            type: SummaryViewPropertyBase.KickedOffDate,
            data: nextOrUpcoming.kickedOffAt,
        });
    }

    if ('submittedAt' in release && release.submittedAt) {
        rows.push({
            type: SummaryViewPropertyBase.SubmittedDate,
            data: release.submittedAt,
        });
    }

    const completedRelease = release as CompletedReleaseSummary;
    if ('releaseDate' in completedRelease && completedRelease.releaseDate) {
        rows.push({
            type: SummaryViewPropertyBase.ReleasedAtDate,
            data: completedRelease.releaseDate,
        });
    }

    if ('appStoreBuilds' in release && release.appStoreBuilds && release.appStoreBuilds.length > 0) {
        rows.push({
            type: SummaryViewPropertyBase.Builds,
            data: release.appStoreBuilds,
        });

        rows.push({
            type: SummaryViewPropertyBase.AdoptionRate,
            data: release.appStoreBuilds,
        });
    }

    // AppHome uses PhasedRelease, but summary view expects DestinationPhasedRelease
    if ('phasedRelease' in release && release.phasedRelease) {
        rows.push({
            type: SummaryViewPropertyBase.PhasedRelease,
            data: release.phasedRelease as any,
        });
    }

    if (release.steps && release.steps.length > 0) {
        release.steps.forEach((step) => {
            if (step.type) {
                rows.push({
                    type: `Archetype:${step.type}` as typeof ArchetypeSummaryViewProperty,
                    data: step as unknown as StepRunStatusData,
                });
            }
        });
    }

    const releasesWithStats = release as NextReleaseSummary | CompletedReleaseSummary;
    if (releasesWithStats.stats && releasesWithStats.stats.length > 0) {
        releasesWithStats.stats.forEach((stat) => {
            rows.push({
                type: `Statistic:${stat.type}` as typeof StatisticSummaryViewProperty,
                data: stat,
            });
        });
    }

    return rows;
};
